import { Injectable } from '@angular/core';

/** What the browser can say about how a visitor arrived. A claim: the server keeps it as one. */
export interface SignupOriginClaim {
  referrer?: string;
  landingPage?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  firstSeen?: string;
}

const STORAGE_KEY = '$ENYDEA_signup_origin';
const MAX_AGE_MS = 30 * 24 * 3600 * 1000;

/**
 * Remembers how the visitor first arrived, so the sign-up can say where it came from even when it
 * happens several pages (or days) later. First touch wins; a later visit only replaces a first
 * touch that carried nothing (a direct visit) with one that does (a referrer or UTM tags).
 *
 * Privacy: only the path of the landing page is kept (its query string can hold tokens), and the
 * referrer is reduced to origin + path.
 */
@Injectable({ providedIn: 'root' })
export class SignupOriginService {
  capture(): void {
    try {
      const fresh = this.read();
      const stored = this.stored();
      if (stored && (this.informative(stored) || !this.informative(fresh))) return;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
    } catch {
      // Storage can be blocked (private window); the sign-up then simply carries no origin.
    }
  }

  /** The origin to send with a registration, or undefined when nothing was captured. */
  get(): SignupOriginClaim | undefined {
    return this.stored() ?? undefined;
  }

  private stored(): SignupOriginClaim | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const value = JSON.parse(raw) as SignupOriginClaim;
      const age = Date.now() - Date.parse(value.firstSeen ?? '');
      return Number.isFinite(age) && age <= MAX_AGE_MS ? value : null;
    } catch {
      return null;
    }
  }

  private read(): SignupOriginClaim {
    const params = new URLSearchParams(window.location.search);
    const utm = (name: string) => params.get(name) ?? undefined;
    return this.compact({
      referrer: this.externalReferrer(),
      landingPage: window.location.pathname,
      utmSource: utm('utm_source'),
      utmMedium: utm('utm_medium'),
      utmCampaign: utm('utm_campaign'),
      utmContent: utm('utm_content'),
      utmTerm: utm('utm_term'),
      firstSeen: new Date().toISOString(),
    });
  }

  private externalReferrer(): string | undefined {
    try {
      if (!document.referrer) return undefined;
      const url = new URL(document.referrer);
      if (url.host === window.location.host) return undefined;
      return `${url.origin}${url.pathname}`;
    } catch {
      return undefined;
    }
  }

  private informative(origin: SignupOriginClaim): boolean {
    return !!(origin.referrer || origin.utmSource || origin.utmMedium || origin.utmCampaign);
  }

  private compact(origin: SignupOriginClaim): SignupOriginClaim {
    return Object.fromEntries(Object.entries(origin).filter(([, v]) => v)) as SignupOriginClaim;
  }
}
